import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { MapPin, User, Building, Send } from "lucide-react";

// Renders a compact label for a deployment snapshot returned by the backend.
export function describeDeployment(dep) {
  if (!dep) return null;
  if (dep.type === "line") return `Line · ${dep.line_name}`;
  if (dep.type === "department") return `Dept · ${dep.department_name}`;
  if (dep.type === "person") {
    const label = dep.person_name || "—";
    return dep.user_id ? `Person · ${label}` : `Person · ${label} (external)`;
  }
  return dep.label || "—";
}

export function DeployAssetDialog({ open, onOpenChange, asset, onDone }) {
  const [type, setType] = useState("line");
  const [lines, setLines] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [lineId, setLineId] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [personMode, setPersonMode] = useState("user"); // "user" | "external"
  const [userId, setUserId] = useState("");
  const [personName, setPersonName] = useState("");
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Load each master list independently so one failure (e.g. blocked /users for
    // a non-admin) never wipes the whole dialog. Each fetch is best-effort.
    (async () => {
      try {
        const ls = await api.get("/deployment-masters/lines", { params: asset?.location ? { location: asset.location } : {} });
        setLines(ls.data || []);
      } catch { toast.error("Failed to load lines"); }
      try {
        const ds = await api.get("/deployment-masters/departments");
        setDepartments(ds.data || []);
      } catch { toast.error("Failed to load departments"); }
      try {
        const us = await api.get("/users/assignable");
        setUsers(us.data || []);
      } catch { /* Users picker is optional — external-person mode still works. */ }
    })();
  }, [open, asset?.location]);

  const currentLabel = useMemo(() => describeDeployment(asset?.deployment), [asset?.deployment]);

  const submit = async () => {
    const payload = { type, effective_date: effectiveDate || undefined, notes: notes.trim() || undefined };
    if (type === "line") {
      if (!lineId) { toast.error("Pick a line"); return; }
      payload.line_id = lineId;
    } else if (type === "department") {
      if (!departmentId) { toast.error("Pick a department"); return; }
      payload.department_id = departmentId;
    } else if (type === "person") {
      if (personMode === "user") {
        if (!userId) { toast.error("Pick a user"); return; }
        payload.user_id = userId;
      } else {
        if (!personName.trim()) { toast.error("Enter the person's name"); return; }
        payload.person_name = personName.trim();
      }
    }
    setBusy(true);
    try {
      await api.post(`/assets/${asset.id}/deploy`, payload);
      toast.success("Deployment recorded");
      onDone && onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Deployment failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="deploy-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Deploy Asset Within Location</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
            <div className="text-xs text-slate-500">Currently</div>
            <div className="font-medium">
              Location: {asset?.location || "—"} · Deployed to: {currentLabel || <span className="text-slate-400">not deployed</span>}
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">Deploy to</Label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              <button
                type="button"
                onClick={() => setType("line")}
                data-testid="deploy-type-line"
                className={`border rounded-md px-3 py-2 text-sm flex items-center justify-center gap-1.5 ${type === "line" ? "border-[#7C1D3A] bg-[#fcf0f3] text-[#7C1D3A]" : "border-slate-200 hover:bg-slate-50"}`}
              >
                <MapPin className="w-4 h-4" /> Line
              </button>
              <button
                type="button"
                onClick={() => setType("person")}
                data-testid="deploy-type-person"
                className={`border rounded-md px-3 py-2 text-sm flex items-center justify-center gap-1.5 ${type === "person" ? "border-[#7C1D3A] bg-[#fcf0f3] text-[#7C1D3A]" : "border-slate-200 hover:bg-slate-50"}`}
              >
                <User className="w-4 h-4" /> Person
              </button>
              <button
                type="button"
                onClick={() => setType("department")}
                data-testid="deploy-type-department"
                className={`border rounded-md px-3 py-2 text-sm flex items-center justify-center gap-1.5 ${type === "department" ? "border-[#7C1D3A] bg-[#fcf0f3] text-[#7C1D3A]" : "border-slate-200 hover:bg-slate-50"}`}
              >
                <Building className="w-4 h-4" /> Department
              </button>
            </div>
          </div>

          {type === "line" && (
            <div>
              <Label>Line *</Label>
              <Select value={lineId} onValueChange={setLineId}>
                <SelectTrigger data-testid="deploy-line-select"><SelectValue placeholder={lines.length ? "Pick a line" : `No lines defined${asset?.location ? ` for '${asset.location}'` : ""} — add one under Company Settings`} /></SelectTrigger>
                <SelectContent>
                  {lines.map((l) => (
                    <SelectItem key={l.id} value={l.id}>{l.name} · {l.location}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {type === "department" && (
            <div>
              <Label>Department *</Label>
              <Select value={departmentId} onValueChange={setDepartmentId}>
                <SelectTrigger data-testid="deploy-department-select"><SelectValue placeholder={departments.length ? "Pick a department" : "No departments defined — add one under Company Settings"} /></SelectTrigger>
                <SelectContent>
                  {departments.map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {type === "person" && (
            <div className="space-y-2">
              <div className="flex gap-2 text-xs">
                <button
                  type="button"
                  onClick={() => setPersonMode("user")}
                  className={`px-2 py-1 rounded ${personMode === "user" ? "bg-[#7C1D3A] text-white" : "bg-slate-100 text-slate-700"}`}
                  data-testid="deploy-person-mode-user"
                >
                  Asset IQ User
                </button>
                <button
                  type="button"
                  onClick={() => setPersonMode("external")}
                  className={`px-2 py-1 rounded ${personMode === "external" ? "bg-[#7C1D3A] text-white" : "bg-slate-100 text-slate-700"}`}
                  data-testid="deploy-person-mode-external"
                >
                  External Person
                </button>
              </div>
              {personMode === "user" ? (
                <div>
                  <Label>User *</Label>
                  <Select value={userId} onValueChange={setUserId}>
                    <SelectTrigger data-testid="deploy-user-select"><SelectValue placeholder="Pick a user" /></SelectTrigger>
                    <SelectContent>
                      {users.map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div>
                  <Label>Person Name *</Label>
                  <Input
                    value={personName}
                    onChange={(e) => setPersonName(e.target.value)}
                    placeholder="e.g. Suresh Kumar (Line Operator)"
                    data-testid="deploy-external-name"
                  />
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div><Label>Effective Date</Label><Input type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} data-testid="deploy-effective-date" /></div>
            <div><Label>&nbsp;</Label></div>
            <div className="col-span-2">
              <Label>Notes</Label>
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional — e.g. re-allocated after Line 2 shutdown" data-testid="deploy-notes" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid="confirm-deploy-btn">
            <Send className="w-4 h-4 mr-2" /> {busy ? "Deploying..." : "Record Deployment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
